"use client";

import React, { useEffect, useRef, useState } from "react";
import Link from "next/link";
import type {
  CarouselSectionData,
  CarouselSlideData,
} from "@/themes/default/templates/section/types";
import { getLocalizedText } from "@/themes/default/templates/section/utils";
import { layerPositionStyle } from "@/themes/default/templates/section/_shared/SlideLayers";

/**
 * Epona "editorial" hero — a full-bleed slider where each slide places a
 * calm, left-aligned content block (mono eyebrow · large light-weight
 * headline with a value-coloured accent · lede · pill CTAs) over a
 * background photo, in the Crystal-Image house style. Optional value dots
 * (Wisdom / Compassion / Integrity) sit beneath the CTAs.
 *
 * Content comes straight from the carousel slide fields — subtitle →
 * eyebrow, title → headline (split on "|" for the accented tail),
 * description → lede, buttons → CTAs — so it's authored in the normal
 * carousel editor. Activated by `section.variant === 'editorial'`.
 */

const DISPLAY = '"Helvetica Neue", "Segoe UI", Arial, sans-serif';
const ACCENT_GRADIENT = "linear-gradient(100deg,#FFC933 0%,#36C6F0 52%,#3DE07A 100%)";

interface Props {
  section: CarouselSectionData & {
    variant?: string;
    highlights?: Array<{ label?: any; color?: string }>;
  };
  currentLanguage?: "en" | "mm";
  /** Server-rendered `type: 'section'` layers, keyed by layer id. */
  sectionNodes?: Record<string, React.ReactNode>;
}

function splitTitle(t: string): [string, string] {
  const i = t.indexOf("|");
  if (i === -1) return [t, ""];
  return [t.slice(0, i).trim(), t.slice(i + 1).trim()];
}

export function HeroEditorial({ section, currentLanguage = "en", sectionNodes }: Props) {
  const slides: CarouselSlideData[] = Array.isArray(section.slides) ? section.slides : [];
  const [active, setActive] = useState(0);

  const autoplay = section.autoplay ?? true;
  const speed = section.autoplaySpeed ?? 8000;
  const dur = section.transitionDuration ?? 700;

  useEffect(() => {
    if (!autoplay || slides.length <= 1) return;
    const id = setInterval(() => setActive((i) => (i + 1) % slides.length), speed);
    return () => clearInterval(id);
  }, [autoplay, speed, slides.length]);

  // Smooth "scroll to content" — jump one screen down, to the section
  // directly beneath the full-height hero.
  const heroRef = useRef<HTMLElement>(null);
  const scrollDown = () => {
    const el = heroRef.current;
    // Scroll the full hero out of view so the next section fills the screen.
    // Native `behavior:'smooth'` is a no-op in some engines, so animate manually.
    const target = el
      ? el.getBoundingClientRect().bottom + window.scrollY - 2
      : window.innerHeight;
    const startY = window.scrollY;
    const dist = target - startY;
    if (Math.abs(dist) < 2) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      window.scrollTo(0, target);
      return;
    }
    const t0 = performance.now();
    const ease = (t: number) => 1 - Math.pow(1 - t, 3);
    const step = (now: number) => {
      const p = Math.min((now - t0) / 620, 1);
      window.scrollTo(0, startY + dist * ease(p));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  if (slides.length === 0) return null;

  const highlights = Array.isArray(section.highlights) ? section.highlights : [];
  const minHeight =
    "calc(100dvh - var(--site-header-h, var(--header-h-default, 128px)))";

  return (
    <section
      ref={heroRef}
      className="relative w-full overflow-hidden"
      style={{ minHeight, backgroundColor: "#08131f" }}
      data-section-id={section._id}
      data-section-type={section.type}
      data-hero-variant="editorial"
    >
      {slides.map((slide, i) => {
        const isActive = i === active;
        const eyebrow = slide.subtitle ? getLocalizedText(slide.subtitle, currentLanguage) : "";
        const [head, headAccent] = splitTitle(
          slide.title ? getLocalizedText(slide.title, currentLanguage) : "",
        );
        const lede = slide.description ? getLocalizedText(slide.description, currentLanguage) : "";
        const buttons = slide.buttons || [];

        return (
          <div
            key={slide.id || i}
            className="absolute inset-0 transition-opacity ease-in-out"
            style={{
              opacity: isActive ? 1 : 0,
              transitionDuration: `${dur}ms`,
              pointerEvents: isActive ? "auto" : "none",
            }}
            aria-hidden={isActive ? undefined : true}
          >
            {/* Background photo */}
            {slide.backgroundImage && (
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage: `url(${slide.backgroundImage})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  transform: isActive ? "scale(1.04)" : "scale(1)",
                  transition: "transform 9s ease-out",
                }}
              />
            )}
            {/* Left-weighted scrim for legibility */}
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(90deg, rgba(6,16,27,0.88) 0%, rgba(6,16,27,0.66) 38%, rgba(6,16,27,0.30) 72%, rgba(6,16,27,0.12) 100%)",
              }}
            />
            <div
              className="absolute inset-x-0 bottom-0 h-1/3"
              style={{ background: "linear-gradient(0deg, rgba(6,16,27,0.55), transparent)" }}
            />

            {/* Content */}
            <div className="relative mx-auto flex h-full max-w-7xl items-center px-5 sm:px-6 lg:px-8"
              style={{ minHeight }}>
              <div className="max-w-3xl py-24">
                {eyebrow && (
                  <p
                    className="mb-5 text-xs font-medium uppercase sm:text-[13px]"
                    style={{ letterSpacing: "0.24em", color: "rgba(224,236,252,0.72)", fontFamily: "ui-monospace, Menlo, monospace" }}
                  >
                    {eyebrow}
                  </p>
                )}
                {(head || headAccent) && (
                  <h1
                    className="text-[2.4rem] leading-[1.05] text-white sm:text-6xl lg:text-[4.6rem]"
                    style={{ fontFamily: DISPLAY, fontWeight: 200, letterSpacing: "-0.02em", textWrap: "balance" as any }}
                  >
                    {head}
                    {headAccent && (
                      <>
                        {" "}
                        <span
                          style={{
                            fontWeight: 500,
                            background: ACCENT_GRADIENT,
                            WebkitBackgroundClip: "text",
                            backgroundClip: "text",
                            color: "transparent",
                          }}
                        >
                          {headAccent}
                        </span>
                      </>
                    )}
                  </h1>
                )}
                {lede && (
                  <p className="mt-6 max-w-xl text-base leading-relaxed sm:text-lg" style={{ color: "rgba(224,236,252,0.85)" }}>
                    {lede}
                  </p>
                )}
                {buttons.length > 0 && (
                  <div className="mt-8 flex flex-wrap gap-3">
                    {buttons.map((b, bi) => {
                      const label = b.text ? getLocalizedText(b.text, currentLanguage) : "";
                      const primary = (b.style || "primary") === "primary";
                      return (
                        <Link
                          key={bi}
                          href={b.url || "#"}
                          target={b.openInNewTab ? "_blank" : undefined}
                          rel={b.openInNewTab ? "noopener noreferrer" : undefined}
                          className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-transform hover:-translate-y-0.5"
                          style={
                            primary
                              ? { backgroundColor: "var(--color-accent)", color: "#fff", boxShadow: "0 12px 28px -12px color-mix(in srgb, var(--color-accent) 70%, transparent)" }
                              : { color: "#fff", border: "1px solid rgba(255,255,255,0.55)" }
                          }
                        >
                          {label}
                        </Link>
                      );
                    })}
                  </div>
                )}
                {highlights.length > 0 && (
                  <div className="mt-11 flex flex-wrap gap-x-7 gap-y-3">
                    {highlights.map((h, hi) => {
                      const label = h.label ? getLocalizedText(h.label, currentLanguage) : "";
                      return (
                        <span
                          key={hi}
                          className="flex items-center gap-2.5 text-xs uppercase"
                          style={{ letterSpacing: "0.14em", color: "rgba(224,236,252,0.7)", fontFamily: "ui-monospace, Menlo, monospace" }}
                        >
                          <i className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: h.color || "#36C6F0" }} />
                          {label}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* Section layers (e.g. the enrollment card) of the ACTIVE slide.
          Rendered outside the fading slide stack and keyed by sectionId, so
          a widget placed on every slide stays put instead of blinking on
          each transition. Positioned inside the content gutter. */}
      {sectionNodes && (
        <div className="pointer-events-none absolute inset-0 z-[15]">
          <div className="relative mx-auto h-full max-w-7xl">
            <div className="absolute inset-y-0 inset-x-5 sm:inset-x-6 lg:inset-x-8">
              {(slides[active]?.layers ?? [])
                .filter((l) => l.type === "section" && sectionNodes[l.id])
                .map((l) => (
                  <div key={String(l.sectionId)} className="pointer-events-auto" style={{ ...layerPositionStyle(l), maxWidth: "100%" }}>
                    {sectionNodes[l.id]}
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Arrows */}
      {(section.showArrows ?? true) && slides.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => setActive((p) => (p - 1 + slides.length) % slides.length)}
            aria-label="Previous slide"
            className="absolute left-4 top-1/2 z-20 -translate-y-1/2 rounded-full border border-white/25 p-2.5 text-white/80 backdrop-blur-sm transition hover:border-white/60 hover:text-white"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
          <button
            type="button"
            onClick={() => setActive((p) => (p + 1) % slides.length)}
            aria-label="Next slide"
            className="absolute right-4 top-1/2 z-20 -translate-y-1/2 rounded-full border border-white/25 p-2.5 text-white/80 backdrop-blur-sm transition hover:border-white/60 hover:text-white"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>
        </>
      )}

      {/* Dots — left-aligned with the content column */}
      {(section.showDots ?? true) && slides.length > 1 && (
        <div className="pointer-events-none absolute inset-x-0 bottom-8 z-20">
          <div className="mx-auto flex max-w-7xl gap-2.5 px-5 sm:px-6 lg:px-8">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setActive(i)}
                aria-label={`Go to slide ${i + 1}`}
                aria-current={i === active}
                className="pointer-events-auto h-2 rounded-full transition-all"
                style={{
                  width: i === active ? "1.9rem" : "0.5rem",
                  backgroundColor: i === active ? "var(--color-accent)" : "rgba(255,255,255,0.45)",
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Scroll-to-content cue — smooth-scrolls one screen down */}
      <button
        type="button"
        onClick={scrollDown}
        aria-label="Scroll to content"
        className="group absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 flex-col items-center gap-1.5 text-white/65 transition-colors hover:text-white"
      >
        <span
          className="text-[10px] uppercase"
          style={{ letterSpacing: "0.24em", fontFamily: "ui-monospace, Menlo, monospace" }}
        >
          Scroll
        </span>
        <span className="flex h-9 w-9 items-center justify-center rounded-full border border-white/30 transition-colors group-hover:border-white/70">
          <svg className="animate-bounce" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>
    </section>
  );
}

export default HeroEditorial;
